import { useState } from "react";

import { useParams } from "react-router-dom";
import { Box, ListItemText } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { DateTime } from "luxon";
import {
    useMediaQuery, useTheme,
    Grid2 as Grid
} from "@mui/material";
import { pink } from "@mui/material/colors";
import {
    LocalizationProvider
} from '@mui/x-date-pickers';
import {
    AdapterLuxon
} from '@mui/x-date-pickers/AdapterLuxon';
import {
    NavigateNext as NavigateNextIcon,
    SmartToy as SmartToyIcon
} from '@mui/icons-material';

import { useMSALPayload } from "@initia-digital/auth-client-utils";
import { baseDefinedAndNotNullNarrowing } from "@initia-digital/ts-utils";
import {
    BasicLoader, FilledButton, IconButton, Textfield,
    Typography, BasicDatePicker, CheckboxListItemButton, EditableDataGrid,
    RowWithId, GeneralChip
} from "@initia-digital/component-library";
import { ISalesforceJob } from "@initia-digital/types/job";

import { fetchJobDetail } from "../api/query";
import { IPlanStageProps } from "../interfaces/componentInterfaces";
import { TagAutoComplete, TeamMembersMultiselectAutoComplete } from "./Autosuggest";
import { useWorkAndSafetyStore } from "../store";
import { PlanActions } from "./PlanActions";
import { SectionHeader } from "./SectionHeader";
import { PlanHeader } from "./PlanHeader";
import { ContactPersonAlert } from "./ContactPersonAlert";
import { v4 as uuidv4 } from 'uuid';
import { ITest, IInstallation } from "@initia-digital/types/work-and-safety";
import {
    TESTS_REQUIRED_COL_DEFS, NEW_TESTS_REQUIRED_ROW_TEMPLATE,
    INSTALLATIONS_REQUIRED_COL_DEFS, NEW_INSTALLATIONS_REQUIRED_ROW_TEMPLATE,
} from "./DataGridConfiguration";

export const PlanOverview = (props: IPlanStageProps) => {
    // -------------------------------
    const {
        handleSave, handleSaveAndNext,
        handleStatusChange,
        handleAiTagGeneration, aiTagGenerationLoading
    } = props;
    const { editPlanState, updateEditPlanState } = useWorkAndSafetyStore();
    const { title, status, startDate, endDate, overrideSiteAddress } = editPlanState;

    // STATE
    const [selectedRows, setSelectedRows] = useState<{
        tests: RowWithId[]
        installations: RowWithId[]
        bookings: RowWithId[]
        contacts: RowWithId[]
    }>({
        tests: [],
        installations: [],
        bookings: [],
        contacts: []
    });

    const { planId, jobId } = useParams();

    const canCalculatedDuration = baseDefinedAndNotNullNarrowing(startDate) && baseDefinedAndNotNullNarrowing(endDate);
    const duration = canCalculatedDuration ? DateTime.fromISO(endDate).endOf('day').diff(DateTime.fromISO(startDate).startOf('day'), 'days', { conversionAccuracy: 'longterm' }) : null;

    const theme = useTheme();
    const isSmallerThanMd = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallerThanSm = useMediaQuery(theme.breakpoints.down('sm'));

    const authDetail = useMSALPayload();

    // JOB DETAIL
    // ---------------------
    const { data: jobDetail } = useQuery<Partial<ISalesforceJob>>({
        queryKey: ['job', jobId],
        queryFn: () => fetchJobDetail(authDetail, jobId!),
        enabled: baseDefinedAndNotNullNarrowing(jobId),
        refetchOnWindowFocus: true,
        retry: 2
    });
    const { address } = jobDetail ?? { address: undefined };
    const useOverrideAddress = ( !baseDefinedAndNotNullNarrowing(address) || address.trim() === '' ) // no job address
                        || baseDefinedAndNotNullNarrowing(overrideSiteAddress) // or, has override job address



    return (
        <Box sx={{ width: '100%' }}>
            <PlanHeader
                id={planId}
                title={title}
                status={status}
            />
            <Box
                display="flex" flexDirection={isSmallerThanSm ? "column" : "row"} flexWrap="wrap"
                alignItems="center" justifyContent="space-between"
                maxWidth="100%"
            >
                <SectionHeader label="Workbrief" />
                <PlanActions
                    handleSave={handleSave}
                    handleStatusChange={handleStatusChange}
                />
            </Box>

            <Textfield
                value={editPlanState.title}
                name="title"
                label="Title"
                minRows={1}
                placeholder="A short descriptive title for this plan"
                handleChange={(event) => {
                    // ---------------------
                    updateEditPlanState({
                        ...editPlanState,
                        title: event.target.value
                    });
                }}
            />

            <Box display="flex" flexDirection="column" mt="12px">
                <Box>
                    <Typography
                        fontSize={"0.8rem"}
                        fontWeight={700}
                        color={theme.palette.primary.contrastText}
                    >
                        Who is involved in the work?
                    </Typography>
                </Box>
                <Box>
                    <Typography
                        fontSize={"0.75rem"}
                        fontWeight={400}
                        color={theme.palette.primary.contrastText}
                    >
                        Initia Team Members
                    </Typography>
                </Box>
                <TeamMembersMultiselectAutoComplete
                    name="teamMembers"
                    handleAutoSuggestSelectStateChange={(event) => {
                        // ---------------------
                        if (Array.isArray(event.target.value) && event.target.value.length === 0) {
                            updateEditPlanState({
                                ...editPlanState,
                                [event.target.name]: event.target.value,
                                ourContact: null
                            });
                        }
                        else {
                            updateEditPlanState({
                                ...editPlanState,
                                [event.target.name]: event.target.value
                            });
                        }
                    }}
                    selectedValues={editPlanState.teamMembers}
                />
            </Box>

            <Box display="flex" flexDirection="column" mt="4px" mb="8px">
                <Box>
                    <Typography
                        fontSize={"0.75rem"}
                        fontWeight={400}
                        color={theme.palette.primary.contrastText}
                    >
                        {editPlanState.serviceClearanceRequired ? "Site Investigation Lead" : "Initia Lead"}
                    </Typography>
                </Box>
                <Typography
                    color={theme.palette.primary.contrastText}
                    fontWeight={300}
                    fontSize="0.65rem"
                >
                    {editPlanState.serviceClearanceRequired 
                        ? "Select the person responsible for site investigation per Initia's Service Clearance SOP"
                        : "Select the Initia lead for this project"
                    }
                </Typography>
                <Box
                    display="flex"
                    flexDirection="row"
                    flexWrap="wrap"
                    gap="2px"
                >
                    {/* Team member options */}
                    {editPlanState.teamMembers
                        .map((member) => (
                            <GeneralChip
                                key={`contact-${member}`}
                                label={member}
                                backgroundColor={editPlanState.ourContact === member ? pink[500] : theme.palette.primary.main}
                                color={editPlanState.ourContact === member ? theme.palette.common.white : theme.palette.primary.contrastText}
                                size="small"
                                clickable
                                handleClick={() => {
                                    updateEditPlanState({
                                        ...editPlanState,
                                        ourContact: editPlanState.ourContact === member ? null : member
                                    });
                                }}
                            />
                        ))}
                </Box>

                <ContactPersonAlert 
                    contactPersonName={editPlanState.ourContact}
                    successMessage={editPlanState.serviceClearanceRequired ? "Site Investigation Lead selected" : "Initia Lead selected"}
                    warningMessage={editPlanState.serviceClearanceRequired ? "Site Investigation Lead has not been selected" : "Initia Lead has not been selected"}
                    showPersonName={true}
                />
            </Box>

            <LocalizationProvider dateAdapter={AdapterLuxon}>
                <Box>
                    <Typography
                        fontSize={"0.8rem"}
                        fontWeight={700}
                        color={theme.palette.primary.contrastText}
                    >
                        When is the work?
                    </Typography>
                </Box>
                <Grid container py="10px" mb="8px">
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Box px="5px">
                            <BasicDatePicker
                                value={baseDefinedAndNotNullNarrowing(editPlanState.startDate) ? DateTime.fromISO(editPlanState.startDate) : null}
                                name="startDate"
                                label="When will the work start?"
                                fontSize="0.8rem"
                                handleChange={(value) => {
                                    // ---------------------
                                    updateEditPlanState({
                                        ...editPlanState,
                                        startDate: value?.toISO() ?? DateTime.local().toISO()
                                    });
                                }}
                            />
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Box px="5px">
                            <BasicDatePicker
                                value={baseDefinedAndNotNullNarrowing(editPlanState.endDate) ? DateTime.fromISO(editPlanState.endDate) : null}
                                name="endDate"
                                label="When will the work end?"
                                fontSize="0.8rem"
                                handleChange={(value) => {
                                    // ---------------------
                                    updateEditPlanState({
                                        ...editPlanState,
                                        endDate: value?.toISO() ?? editPlanState.startDate
                                    });
                                }}
                            />
                        </Box>
                    </Grid>
                    {
                        canCalculatedDuration &&
                        <Grid size={{ xs: 12, md: 4 }}>
                            <Box px="5px" py={"10px"} pb={isSmallerThanMd ? "10px" : "0px"}>
                                <Typography color={theme.palette.primary.contrastText} fontWeight={300} fontSize="0.8rem">
                                    {`Duration: `}
                                    <strong>
                                        {`${Math.round(duration!.days)} day${Math.round(duration!.days) === 1 ? '' : 's'}`}
                                    </strong>
                                </Typography>
                            </Box>
                        </Grid>
                    }
                </Grid>
            </LocalizationProvider>

            <Box px="5px">
                <Box>
                    <Typography
                        fontSize={"0.8rem"}
                        fontWeight={700}
                        color={theme.palette.primary.contrastText}
                    >
                        Where is the work?
                    </Typography>
                </Box>
                <Typography color={theme.palette.primary.contrastText} fontWeight={300} fontSize="0.8rem">
                    {baseDefinedAndNotNullNarrowing(address) ? `Job address: ${address}` : 'No site address for job - please add one below'}
                </Typography>
            </Box>

            <Box>
                <CheckboxListItemButton
                    isSelected={useOverrideAddress}
                    onSelect={() => {
                        // ---------------------
                        updateEditPlanState({
                            ...editPlanState,
                            overrideSiteAddress: baseDefinedAndNotNullNarrowing(overrideSiteAddress) ? null : ""
                        });
                    }}
                    label={
                        <ListItemText>
                            <Typography
                                fontSize={"0.75rem"}
                                fontWeight={300}
                                color={theme.palette.primary.contrastText}
                            >
                                Use different address
                            </Typography>
                        </ListItemText>
                    }
                />

                {useOverrideAddress && (
                    <Textfield
                        fontSize="0.8rem"
                        value={editPlanState.overrideSiteAddress ?? ''}
                        name="overrideSiteAddress"
                        label="Site Address"
                        minRows={1}
                        placeholder="Enter the site address for this work"
                        handleChange={(event) => {
                            updateEditPlanState({
                                ...editPlanState,
                                overrideSiteAddress: event.target.value
                            });
                        }}
                    />
                )}
            </Box>

            <Box mt="10px">
                <Typography
                    fontSize={"0.8rem"}
                    fontWeight={700}
                    color={theme.palette.primary.contrastText}
                >
                    What is the work?
                </Typography>
            </Box>
            <Textfield
                fontSize="0.8rem"
                value={editPlanState.description}
                name="description"
                label="Describe the work"
                minRows={3}
                placeholder="Describe in detail what is needing to be done so that it is clear and concise to those executing the work. If there are specific locations or addresses within the site that need attention, please include these details here."
                handleChange={(event) => {
                    // ---------------------
                    updateEditPlanState({
                        ...editPlanState,
                        description: event.target.value
                    });
                }}
            />

            {
                baseDefinedAndNotNullNarrowing(authDetail) &&
                baseDefinedAndNotNullNarrowing(editPlanState.description) && editPlanState.description.split(/\s+/).length > 10 &&
                <Box display="flex" flexDirection="row" mb="5px" alignItems="center">
                    <Box p="5px">
                        <Box
                            display="flex"
                            alignItems="center"
                        >
                            <IconButton
                                tooltipTitle={`Generate tags from description`}
                                onClick={handleAiTagGeneration}
                                backgroundColor={theme.palette.secondary.main}
                                color={theme.palette.secondary.contrastText}
                            >
                                <SmartToyIcon fontSize="medium" />
                            </IconButton>
                            <Box pl="8px">
                                <Typography fontSize="0.8rem">
                                    {
                                        aiTagGenerationLoading ?
                                            "Generating tags..." :
                                            "Generate tags from description"
                                    }
                                </Typography>
                            </Box>
                            <Box minWidth="50px" minHeight="50px">
                                {
                                    aiTagGenerationLoading &&
                                    <BasicLoader
                                        size={50}
                                    />
                                }
                            </Box>
                        </Box>
                    </Box>
                </Box>
            }

            <TagAutoComplete
                name="objectives"
                type="objective"
                handleAutoSuggestSelectStateChange={(event) => {
                    // ---------------------
                    updateEditPlanState({
                        ...editPlanState,
                        [event.target.name]: event.target.value
                    });
                }}
                selectedValues={editPlanState.objectives}
            />

            <Box display="flex" flexDirection="column" mt="12px" mb="30px">
                <Box>
                    <Typography
                        fontSize={"0.8rem"}
                        fontWeight={700}
                        color={theme.palette.primary.contrastText}
                    >
                        Test Schedule
                    </Typography>
                </Box>
                <CheckboxListItemButton
                    isSelected={editPlanState.testsRequired}
                    onSelect={() => {
                        // ---------------------
                        updateEditPlanState({
                            ...editPlanState,
                            testsRequired: !editPlanState.testsRequired
                        });
                    }}
                    label={
                        <ListItemText>
                            <Typography
                                fontSize={"0.75rem"}
                                fontWeight={300}
                                color={theme.palette.primary.contrastText}
                            >
                                Are any tests required?
                            </Typography>
                        </ListItemText>
                    }
                />
                {
                    editPlanState.testsRequired
                    && (
                        <Box>
                            <EditableDataGrid<ITest>
                                density="compact"
                                singleClickEdit
                                disableColumnMenu
                                disableColumnSorting
                                disableColumnResize
                                hideFooter
                                rows={editPlanState.testSchedule.rows}
                                columns={TESTS_REQUIRED_COL_DEFS}
                                multilineFields={['testNotes']}
                                onRowsChange={(newRows) => {
                                    // ---------------------
                                    updateEditPlanState({
                                        ...editPlanState,
                                        testSchedule: {
                                            rows: newRows
                                        }
                                    });
                                }}
                                onRowSelection={(updateSelectedRows) => {
                                    // ---------------------
                                    setSelectedRows({
                                        ...selectedRows,
                                        tests: updateSelectedRows
                                    });
                                }}
                                generateNewId={() => uuidv4()}
                                addRowTemplate={{
                                    ...NEW_TESTS_REQUIRED_ROW_TEMPLATE,
                                    jobId: jobId ?? '',
                                }}
                            />
                        </Box>
                    )
                }
            </Box>

            <Box display="flex" flexDirection="column" mt="12px" mb="30px">
                <Box>
                    <Typography
                        fontSize={"0.8rem"}
                        fontWeight={700}
                        color={theme.palette.primary.contrastText}
                    >
                        Installations
                    </Typography>
                </Box>
                <CheckboxListItemButton
                    isSelected={editPlanState.installationsRequired}
                    onSelect={() => {
                        // ---------------------
                        updateEditPlanState({
                            ...editPlanState,
                            installationsRequired: !editPlanState.installationsRequired
                        });
                    }}
                    label={
                        <ListItemText>
                            <Typography
                                fontSize={"0.75rem"}
                                fontWeight={300}
                                color={theme.palette.primary.contrastText}
                            >
                                Are any installations required?
                            </Typography>
                        </ListItemText>
                    }
                />
                {
                    editPlanState.installationsRequired
                    && (
                        <Box>
                            <EditableDataGrid<IInstallation>
                                density="compact"
                                singleClickEdit
                                disableColumnMenu
                                disableColumnSorting
                                disableColumnResize
                                hideFooter
                                rows={editPlanState.installationSchedule.rows}
                                columns={INSTALLATIONS_REQUIRED_COL_DEFS}
                                multilineFields={['installationNotes']}
                                onRowsChange={(newRows) => {
                                    // ---------------------
                                    updateEditPlanState({
                                        ...editPlanState,
                                        installationSchedule: {
                                            rows: newRows
                                        }
                                    });
                                }}
                                onRowSelection={(updateSelectedRows) => {
                                    // ---------------------
                                    setSelectedRows({
                                        ...selectedRows,
                                        contacts: updateSelectedRows
                                    });
                                }}
                                generateNewId={() => uuidv4()}
                                addRowTemplate={{
                                    ...NEW_INSTALLATIONS_REQUIRED_ROW_TEMPLATE,
                                    jobId: jobId ?? '',
                                }}
                            />
                        </Box>
                    )
                }
            </Box>

            <Box display="flex" flexDirection="column" mt="12px">
                <Box>
                    <Typography
                        fontSize={"0.8rem"}
                        fontWeight={700}
                        color={theme.palette.primary.contrastText}
                    >
                        Reinstatement requirements
                    </Typography>
                </Box>
                <Box my="4px">
                    <Textfield
                        value={editPlanState.reinstatementRequirements ?? ''}
                        fontSize="0.8rem"
                        name="reinstatementRequirements"
                        minRows={3}
                        label="Describe any reinstatement requirements"
                        placeholder="Describe any reinstatement requirements"
                        handleChange={(event) => {
                            // ---------------------
                            updateEditPlanState({
                                ...editPlanState,
                                reinstatementRequirements: event.target.value
                            });
                        }}
                    />
                </Box>
            </Box>

            <Box display="flex" flexDirection="column" mt="12px">
                <Box>
                    <Typography
                        fontSize={"0.8rem"}
                        fontWeight={700}
                        color={theme.palette.primary.contrastText}
                    >
                        Survey requirements
                    </Typography>
                </Box>
                <Box my="4px">
                    <Textfield
                        value={editPlanState.surveyRequirements ?? ''}
                        fontSize="0.8rem"
                        name="surveyRequirements"
                        minRows={3}
                        label="Describe any survey requirements"
                        placeholder="Describe any survey requirements"
                        handleChange={(event) => {
                            // ---------------------
                            updateEditPlanState({
                                ...editPlanState,
                                surveyRequirements: event.target.value
                            });
                        }}
                    />
                </Box>
            </Box>

            <Textfield
                value={editPlanState.supportingContext}
                name="supportingContext"
                label="Other relevant information"
                minRows={1}
                placeholder="Any other supporting detail (e.g. site access or specific induction requirements)"
                handleChange={(event) => {
                    // ---------------------
                    updateEditPlanState({
                        ...editPlanState,
                        supportingContext: event.target.value
                    });
                }}
            />



            <Box
                width="100%"
                display="flex" flexDirection="row" justifyContent="flex-end"
                pt="12px"
            >
                <FilledButton
                    onClick={handleSaveAndNext}
                    endIcon={<NavigateNextIcon />}
                >
                    Save and Next
                </FilledButton>
            </Box>
        </Box>
    )
}